/**
 * MarkdownRenderer - Reusable component for rendering AI responses with proper markdown support
 * Handles code blocks, syntax highlighting, tables, lists, headers, and more
 */
import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';

// Custom dark theme that matches the app's purple/dark aesthetic
const customCodeStyle = {
  ...oneDark,
  'pre[class*="language-"]': {
    ...oneDark['pre[class*="language-"]'],
    background: 'rgba(0, 0, 0, 0.4)',
    borderRadius: '0.5rem',
    padding: '1rem',
    margin: '0.5rem 0',
    border: '1px solid rgba(139, 92, 246, 0.2)',
  },
  'code[class*="language-"]': {
    ...oneDark['code[class*="language-"]'],
    background: 'transparent',
    fontSize: '0.85rem',
  },
};

/**
 * Convert raw URLs in text to Markdown links
 * Matches URLs that are not already inside Markdown link syntax
 * @param {string} text - The text to process
 * @returns {string} - Text with raw URLs converted to Markdown links
 */
const convertRawUrlsToLinks = (text) => {
  if (!text) return text;
  // Match URLs that are NOT already in Markdown link syntax [text](url) or <url>
  // This regex finds http/https URLs that are not preceded by ]( or <
  const urlRegex = /(?<!\]\()(?<!<)(https?:\/\/[^\s<>\[\]()]+)/g;
  return text.replace(urlRegex, (url) => {
    // Clean trailing punctuation that's likely not part of the URL
    const cleanUrl = url.replace(/[.,;:!?)]+$/, '');
    const trailing = url.slice(cleanUrl.length);
    // Truncate display text for very long URLs
    const displayUrl = cleanUrl.length > 50 ? cleanUrl.substring(0, 47) + '...' : cleanUrl;
    return `[${displayUrl}](${cleanUrl})${trailing}`;
  });
};

/**
 * MarkdownRenderer Component
 * @param {string} content - The markdown content to render
 * @param {string} className - Additional CSS classes
 * @param {string} variant - Style variant: 'default', 'compact', 'chat'
 */
const MarkdownRenderer = memo(({ content, className = '', variant = 'default' }) => {
  if (!content) return null;

  // Safety: ensure content is a string (prevents React error #130)
  let safeContent = typeof content === 'string' ? content :
    typeof content === 'object' ? JSON.stringify(content, null, 2) : String(content);

  // Convert raw URLs to clickable Markdown links
  safeContent = convertRawUrlsToLinks(safeContent);

  // Base classes for different variants
  // Chat variant has extra tight spacing to prevent AI responses from being too spread out
  const variantClasses = {
    default: 'prose prose-invert prose-purple max-w-none',
    compact: 'prose prose-invert prose-purple prose-sm max-w-none',
    chat: 'prose prose-invert prose-purple prose-sm max-w-none leading-snug [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0 [&_p]:mb-1.5',
  };

  const baseClass = variantClasses[variant] || variantClasses.default;

  return (
    <div className={`markdown-renderer ${baseClass} ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Code blocks with syntax highlighting
          code({ node, inline, className: codeClassName, children, ...props }) {
            const match = /language-(\w+)/.exec(codeClassName || '');
            const language = match ? match[1] : '';
            const codeString = String(children).replace(/\n$/, '');

            if (!inline && (match || codeString.includes('\n'))) {
              return (
                <div className={`relative group ${variant === 'chat' ? 'my-2' : 'my-3'}`}>
                  {language && (
                    <div className="absolute top-0 right-0 px-2 py-1 text-xs text-purple-400 bg-black/50 rounded-bl-lg rounded-tr-lg border-l border-b border-purple-500/20">
                      {language}
                    </div>
                  )}
                  <SyntaxHighlighter
                    style={customCodeStyle}
                    language={language || 'text'}
                    PreTag="div"
                    customStyle={{
                      background: 'rgba(0, 0, 0, 0.4)',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                      paddingTop: language ? '2rem' : '1rem',
                      margin: '0',
                      border: '1px solid rgba(139, 92, 246, 0.2)',
                      fontSize: '0.85rem',
                    }}
                    codeTagProps={{
                      style: {
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      }
                    }}
                    {...props}
                  >
                    {codeString}
                  </SyntaxHighlighter>
                </div>
              );
            }

            // Inline code
            return (
              <code
                className="bg-black/30 px-1.5 py-0.5 rounded text-purple-300 text-[0.9em] font-mono border border-purple-500/10"
                {...props}
              >
                {children}
              </code>
            );
          },

          // Headers with proper styling
          h1: ({ children }) => (
            <h1 className={`text-xl font-bold text-white pb-1 border-b border-purple-500/30 ${variant === 'chat' ? 'mt-3 mb-2' : 'mt-6 mb-4'}`}>
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className={`text-lg font-bold text-white ${variant === 'chat' ? 'mt-2 mb-1' : 'mt-5 mb-3 pb-1 border-b border-purple-500/20'}`}>
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className={`text-base font-semibold text-purple-200 ${variant === 'chat' ? 'mt-2 mb-1' : 'mt-4 mb-2'}`}>
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className={`text-sm font-semibold text-purple-300 ${variant === 'chat' ? 'mt-1 mb-1' : 'mt-3 mb-2'}`}>
              {children}
            </h4>
          ),

          // Paragraphs - compact for chat, relaxed for default
          p: ({ children }) => (
            <p className={`text-gray-300 ${variant === 'chat' ? 'leading-snug mb-1.5' : 'leading-relaxed mb-3'}`}>
              {children}
            </p>
          ),

          // Bold and italic
          strong: ({ children }) => (
            <strong className="font-bold text-purple-200">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic text-purple-300">{children}</em>
          ),

          // Lists - compact spacing to prevent excessive whitespace
          ul: ({ children }) => (
            <ul className={`list-disc list-outside text-gray-300 pl-5 ${variant === 'chat' ? 'my-1.5 space-y-0.5' : 'my-2 space-y-1'}`}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className={`list-decimal list-outside text-gray-300 pl-5 ${variant === 'chat' ? 'my-1.5 space-y-0.5' : 'my-2 space-y-1'}`}>
              {children}
            </ol>
          ),
          li: ({ children }) => (
            // Remove paragraph margins inside list items to prevent excessive spacing
            <li className="text-gray-300 leading-snug [&>p]:mb-0 [&>p]:inline">
              {children}
            </li>
          ),

          // Links
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 underline underline-offset-2 transition-colors"
            >
              {children}
            </a>
          ),

          // Blockquotes
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-purple-500/50 pl-4 my-4 italic text-gray-400 bg-purple-500/5 py-2 rounded-r">
              {children}
            </blockquote>
          ),

          // Tables
          table: ({ children }) => (
            <div className="overflow-x-auto my-4 rounded-lg border border-purple-500/20">
              <table className="min-w-full divide-y divide-purple-500/20">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-purple-500/10">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="divide-y divide-purple-500/10 bg-black/20">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="hover:bg-purple-500/5 transition-colors">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-4 py-3 text-left text-xs font-semibold text-purple-300 uppercase tracking-wider">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">
              {children}
            </td>
          ),

          // Horizontal rule
          hr: () => (
            <hr className="my-6 border-t border-purple-500/30" />
          ),

          // Images
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt}
              className="max-w-full h-auto rounded-lg my-4 border border-purple-500/20"
            />
          ),
        }}
      >
        {safeContent}
      </ReactMarkdown>
    </div>
  );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';

export default MarkdownRenderer;
